/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 13 2025
 **/

import { useRef } from "react";
import videojs from "video.js";

import { EventHandler } from "@rfice/eventhandler";

import { Utility } from "@/frameworks/libraries/utilities";

type ProgressControl = {
	el: () => HTMLElement;
};

type VolumePanel = {
	muteToggle: videojs.EventTarget;
	volumeControl: { el: () => HTMLDivElement };
};

type ControlBar = {
	progressControl: ProgressControl;
	volumePanel: VolumePanel;
};

type VideoNode = HTMLVideoElement | null;

type UpdateType = "play" | "time" | "volume";

export default function useVideoPlayerEventListners() {
	const isPlaying = useRef(true);
	const isMuted = useRef(Utility.isMobile);
	const isChanging = useRef(false);

	/* Add */
	const addVideoNodeEventHandlers = (videoNode: VideoNode) => {
		if (!videoNode) return;
		videoNode.addEventListener("mouseup", (e) => e.stopPropagation(), true);
		videoNode.addEventListener("dblclick", (e) => e.stopPropagation(), true);
	};

	const addControlbarEventHandlers = (videoPlayer: videojs.Player) => {
		const { progressControl, volumePanel } = videoPlayer.controlBar as unknown as ControlBar;
		initPlayListner(videoPlayer);
		initTimeListner(videoPlayer, progressControl);
		initVolumeListner(videoPlayer, volumePanel);
	};

	/* Update */
	const updateVideoStatus = (videoNode: VideoNode, type: UpdateType, value: number | boolean | undefined) => {
		if (!videoNode) return;
		switch (type) {
			case "play": {
				if (value === undefined) return;
				if (value as boolean) videoNode.play();
				else videoNode.pause();
				break;
			}
			case "time": {
				videoNode.currentTime = value as number;
				break;
			}
			case "volume":
				videoNode.volume = value as number;
				break;
		}
	};

	/* Init */
	const initPlayListner = (videoPlayer: videojs.Player) => {
		const handleUpdatePlay = (isPlay: boolean) => {
			if (isPlaying.current === isPlay || isChanging.current) return;
			isPlaying.current = isPlay;
			EventHandler.fire("$VideoPlayer::PlayUpdated$", isPlay);
		};

		videoPlayer.on("play", () => handleUpdatePlay(true));
		videoPlayer.on("pause", () => handleUpdatePlay(false));
	};

	const initTimeListner = (videoPlayer: videojs.Player, progressControl: ProgressControl) => {
		const progressSlider = progressControl.el();

		progressSlider.addEventListener(
			"mousedown",
			() => {
				isChanging.current = true;
			},
			true
		);

		progressSlider.addEventListener(
			"mouseup",
			() => {
				if (!isChanging.current) return;
				isChanging.current = false;
				EventHandler.fire("$VideoPlayer::TimeUpdated$", videoPlayer.currentTime());
			},
			true
		);
	};

	const initVolumeListner = (videoPlayer: videojs.Player, volumePanel: VolumePanel) => {
		const muteToggle = volumePanel.muteToggle;
		const volumeSlider = volumePanel.volumeControl.el();

		muteToggle.on("click", () => {
			const muted = videoPlayer.muted();
			if (isMuted.current === muted) return;
			isMuted.current = muted;
			EventHandler.fire("$VideoPlayer::VolumeUpdated$", muted ? 0 : videoPlayer.volume());
		});

		volumeSlider.addEventListener("mousedown", () => {
			isChanging.current = true;
		});

		volumeSlider.addEventListener("mouseup", () => {
			if (!isChanging.current) return;
			isChanging.current = false;
			EventHandler.fire("$VideoPlayer::VolumeUpdated$", videoPlayer.volume());
		});
	};

	return { addVideoNodeEventHandlers, addControlbarEventHandlers, updateVideoStatus };
}
